import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

/**
 * Weekly per-agent engagement numbers (issue #240) — the figures Ankur collects by hand.
 *
 * Per week, per agent: how many conversations, how much talking they represent, and how
 * much substance came out of them ("information load" = entities per 1,000 words).
 *
 * The raw inputs are columns in their own right, not just the derived ratio. That is
 * deliberate: a density metric no one can check is a number people stop trusting the
 * first time it looks wrong. Entities and words are both on screen and both in the CSV,
 * so entities_per_1k_words can be recomputed from the row it sits on.
 *
 * The TOTAL row comes from the server, not from summing this table. Information load is a
 * ratio — a week's total is (all entities / all words), which is NOT the mean of the
 * per-agent ratios, or an agent with three conversations weighs as much as one with
 * thirty.
 */

const S = {
  section: { marginBottom: '32px' },
  h3: { marginBottom: '4px', fontSize: '18px', color: '#1a1a1a' },
  hint: { fontSize: '13px', color: '#6c757d', marginBottom: '16px' },
  bar: { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' },
  input: { padding: '7px 10px', border: '1px solid #ced4da', borderRadius: '6px', fontSize: '13px' },
  btn: { padding: '7px 16px', borderRadius: '6px', border: 'none', background: '#1e3a5f',
         color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 600 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px', background: '#fff' },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e9ecef', color: '#6c757d',
        fontSize: '11px', textTransform: 'uppercase', fontWeight: 700, whiteSpace: 'nowrap' },
  td: { padding: '7px 10px', borderBottom: '1px solid #f1f3f5' },
  num: { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  weekRow: { background: '#f1f3f5', fontWeight: 700 },
  totalRow: { background: '#f8f9fa', fontWeight: 700, borderTop: '2px solid #dee2e6' },
  err: { padding: '10px 12px', background: '#fdecea', border: '1px solid #f5c2c0',
         borderRadius: '6px', fontSize: '13px', marginBottom: '12px', color: '#8a1c17' },
  muted: { color: '#868e96', fontSize: '12px' },
  scroll: { maxHeight: '620px', overflowY: 'auto', border: '1px solid #e9ecef', borderRadius: '8px' },
};

// One definition of the columns, used for the table head, the body, and the CSV — so an
// exported file cannot drift from what the page shows.
const COLUMNS = [
  { key: 'agent_name', label: 'Agent', num: false },
  { key: 'conversations', label: 'Conversations', num: true },
  { key: 'total_minutes', label: 'Total min', num: true },
  { key: 'avg_minutes', label: 'Avg min', num: true },
  { key: 'total_words', label: 'Total words', num: true },
  { key: 'avg_words', label: 'Avg words', num: true },
  { key: 'entities', label: 'Entities', num: true },
  { key: 'entities_per_1k_words', label: 'Entities / 1k words', num: true },
];

const fmt = (v) => (v === null || v === undefined ? '–'
  : typeof v === 'number' ? v.toLocaleString() : v);

/** RFC-4180 quoting: a field containing a comma, quote or newline is quoted and its
 *  quotes doubled. Agent names are free text, so this is not hypothetical. */
const csvCell = (v) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function AgentEngagement({ org }) {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState([]);
  const [basis, setBasis] = useState('');
  const [weeks, setWeeks] = useState('12');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    axios.get('/api/dachido/agent-engagement',
              { params: { ...(org ? { pipeline: org } : {}), weeks } })
      .then(res => {
        setRows(res.data.rows || []);
        setTotals(res.data.totals || []);
        setBasis(res.data.entity_basis || '');
      })
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, [org, weeks]);

  useEffect(() => { load(); }, [load]);

  // Weeks newest-first, each with its agents and its server-computed total.
  const byWeek = useMemo(() => {
    const totalFor = Object.fromEntries(totals.map(t => [t.week_start, t]));
    const order = [];
    const grouped = {};
    rows.forEach(r => {
      if (!grouped[r.week_start]) { grouped[r.week_start] = []; order.push(r.week_start); }
      grouped[r.week_start].push(r);
    });
    return order.map(w => ({ week: w, agents: grouped[w], total: totalFor[w] }));
  }, [rows, totals]);

  const downloadCsv = () => {
    const header = ['week_start', ...COLUMNS.map(c => c.key)];
    const lines = [header.join(',')];
    byWeek.forEach(({ week, agents, total }) => {
      agents.forEach(r => lines.push(
        [week, ...COLUMNS.map(c => r[c.key])].map(csvCell).join(',')));
      if (total) lines.push(
        [week, 'TOTAL', ...COLUMNS.slice(1).map(c => total[c.key])].map(csvCell).join(','));
    });
    // \r\n and a BOM so Excel opens it as UTF-8 without mangling agent names.
    const blob = new Blob(['﻿' + lines.join('\r\n')],
                          { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agent-engagement-${org || 'org'}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <section style={S.section}>
      <h3 style={S.h3}>Agent engagement (weekly)</h3>
      <div style={S.hint}>
        Conversations, talk time and information load per agent per week.
        Information load is entities ÷ words, shown per 1,000 words; both inputs are
        columns here and in the CSV so any figure can be rechecked.
        {basis === 'unique_per_conversation' && (
          <> Entities are counted <strong>once per conversation</strong> — an entity said
          five times in one call counts once.</>
        )}
        {basis === 'all_mentions' && (
          <> This org counts <strong>every mention</strong>, including repeats within a call.</>
        )}
      </div>

      {error && <div style={S.err}>{error}</div>}

      <div style={S.bar}>
        <label style={{ fontSize: '13px', fontWeight: 600 }}>Weeks back</label>
        <select style={S.input} value={weeks} onChange={e => setWeeks(e.target.value)}>
          {['4', '8', '12', '26', '52'].map(w => <option key={w} value={w}>{w}</option>)}
        </select>
        <button style={S.btn} onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
        <button style={{ ...S.btn, background: '#1a7f4b' }}
                onClick={downloadCsv} disabled={loading || !rows.length}>
          Download CSV
        </button>
        {!loading && <span style={S.muted}>{rows.length} agent-weeks</span>}
      </div>

      {!loading && !rows.length && !error && (
        <div style={S.hint}>No conversations in this window.</div>
      )}

      {!!rows.length && (
        <div style={S.scroll}>
          <table style={S.table}>
            <thead>
              <tr>
                {COLUMNS.map(c => (
                  <th key={c.key} style={{ ...S.th, ...(c.num ? S.num : {}) }}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {byWeek.map(({ week, agents, total }) => (
                <React.Fragment key={week}>
                  <tr style={S.weekRow}>
                    <td style={S.td} colSpan={COLUMNS.length}>
                      Week of {week}
                      {total && <span style={{ ...S.muted, fontWeight: 400, marginLeft: '8px' }}>
                        {total.agents} agent{total.agents === 1 ? '' : 's'}
                      </span>}
                    </td>
                  </tr>
                  {agents.map(r => (
                    <tr key={`${week}-${r.agent_id}`}>
                      {COLUMNS.map(c => (
                        <td key={c.key} style={{ ...S.td, ...(c.num ? S.num : {}) }}>
                          {fmt(r[c.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {total && (
                    <tr style={S.totalRow}>
                      <td style={S.td}>Total</td>
                      {COLUMNS.slice(1).map(c => (
                        <td key={c.key} style={{ ...S.td, ...S.num }}>{fmt(total[c.key])}</td>
                      ))}
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default AgentEngagement;
